import React, { useState, useEffect, useRef } from "react";
import {
  FaArrowLeft,
  FaHeart,
  FaRegHeart,
  FaUserCheck,
  FaMapMarkerAlt,
  FaBriefcase,
  FaGraduationCap,
  FaRulerVertical,
  FaPrayingHands,
  FaLanguage,
  FaCamera,
  FaPhoneAlt,
  FaEnvelope,
  FaLock,
  FaBirthdayCake,
  FaWeight,
  FaVenusMars,
  FaHome,
  FaUsers,
  FaStar,
  FaIdCard,
  FaMoneyBillWave,
  FaUniversity,
  FaCity,
  FaGlobe,
  FaInfoCircle,
  FaCalendarAlt,
  FaTint,
  FaSmoking,
  FaWineGlassAlt,
  FaUtensils,
  FaSuitcase,
  FaUserFriends,
  FaShieldAlt,
  FaEye,
  FaCheckCircle,
} from "react-icons/fa";
import { motion } from "framer-motion";
import api from "../../services/api";

const ProfileDetails = ({ profileId, onBack, onOpenConversation, onUpgradeClick }) => {
  const [isShortlisted, setIsShortlisted] = useState(false);
  const [contactRevealed, setContactRevealed] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingInterest, setSendingInterest] = useState(false);
  const [showScreenshotWarning, setShowScreenshotWarning] = useState(false);
  const [userPlan, setUserPlan] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [limitError, setLimitError] = useState(null);
  const [planFeatures, setPlanFeatures] = useState({});



  const [contactError, setContactError] = useState("");
  const BASE_URL = api.defaults.baseURL;
  const baseUrl = BASE_URL.replace("/api", "");

  const profileRef = useRef(null);

  const checkIfShortlisted = async () => {
    try {
      const response = await api.get("/shortlisted");
      const shortlistedIds = response.data.map((p) => p._id);
      setIsShortlisted(shortlistedIds.includes(profile._id));
    } catch (error) {
      console.error("Error checking shortlist:", error);
    }
  };
  useEffect(() => {
    if (!profileId) return;

    const fetchProfileById = async () => {
      setLoadingProfile(true);
      try {
        const res = await api.get(`/profile/${profileId}`);
        setProfile(res.data.profile || res.data);
        setUserPlan(res.data.currentPlan);
        setPlanFeatures(res.data.planFeatures || {});

        await api.post("/record-visit", { visitedProfileId: profileId });
      } catch (error) {
        const data = error.response?.data;

        if (error.response?.status === 403 && data?.limitExceeded) {
          setLimitError(data); // ⬅️ STORE BACKEND MESSAGE
          return;
        }

        console.error("Failed to fetch profile", error);
        onBack();
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfileById();
  }, [profileId]);

  useEffect(() => {
    if (profile && profileRef.current) {
      profileRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [profile]);

  useEffect(() => {
    if (profile) {
      checkIfShortlisted();
      fetchUserPlan();
    }
  }, [profile]);

  const fetchUserPlan = async () => {
    try {
      const response = await api.get("/me");
      setUserPlan(response.data.subscriptionPlan);
    } catch (error) {
      console.error("Error fetching user plan:", error);
    }
  };

  const recordVisit = async () => {
    try {
      await api.post("/record-visit", { visitedProfileId: profile._id });
    } catch (error) {
      console.error("Error recording visit:", error);
      if (error.response?.status === 403 && error.response?.data?.limitExceeded) {
        setLimitError(error.response.data);
      } else {
        onBack();
      }
    }
  };

  const handleShortlist = async () => {
    try {
      const response = await api.post("/shortlist", { profileId: profile._id });
      setIsShortlisted(!isShortlisted);
    } catch (error) {
      console.error("Error shortlisting:", error);
    }
  };

  const handleRevealContact = async () => {
    setLoading(true);
    setContactError("");

    try {
      const response = await api.post("/reveal-contact", {
        profileId: profile._id,
      });

      if (response.data.success) {
        setContactInfo({
          mobile: response.data.mobile,
          email: response.data.email,
        });
        setContactRevealed(true);
      }
    } catch (error) {
      // Read backend error message safely
      const message =
        error.response?.data?.message ||
        "Unable to reveal contact details. Please try again.";

      setContactError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "PrintScreen") {
        setShowScreenshotWarning(true);
        setTimeout(() => setShowScreenshotWarning(false), 2000);
      }
    };

    window.addEventListener("keyup", handleKeyDown);
    return () => window.removeEventListener("keyup", handleKeyDown);
  }, []);
  if (limitError) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center px-4">
        <div className="bg-white max-w-md w-full rounded-2xl shadow-xl p-6 text-center animate-fade-in">

          <div className="mx-auto mb-4 w-14 h-14 flex items-center justify-center rounded-full bg-rose-100">
            <FaLock className="text-rose-600 text-2xl" />
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Daily View Limit Reached
          </h2>

          <p className="text-gray-600 text-sm mb-6">
            {limitError.message ||
              "You’ve reached your daily profile view limit. Upgrade to continue viewing more profiles."}
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={onUpgradeClick}
              className="w-full py-3 bg-rose-600 text-white font-semibold rounded-lg hover:bg-rose-700 transition"
            >
              Upgrade Now
            </button>

            <button
              onClick={onBack}
              className="w-full py-2 text-gray-600 hover:text-gray-900 text-sm"
            >
              Go Back to Matches
            </button>
          </div>
        </div>
      </div>
    );
  }


  if (!profile) return null;

  const calculateExactAge = (dobString) => {
    if (!dobString) return 'N/A';
    const dob = new Date(dobString);
    const today = new Date();
    let ageVal = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      ageVal--;
    }
    return ageVal;
  };
  const age = calculateExactAge(profile.basicInfo?.dob);

  if (loadingProfile) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-rose-600"></div>
      </div>
    );
  }


  const BlurWrapper = ({ locked, children }) => (
    <div className="relative">
      <div className={locked ? "filter blur-md select-none pointer-events-none" : ""}>
        {children}
      </div>

      {locked && (
        <div className="absolute inset-0 flex items-center justify-center">
          <FaLock className="text-white text-3xl bg-black/60 p-3 rounded-full" />
        </div>
      )}
    </div>
  );



  const LockedField = ({ label, icon, value, isLocked }) => (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
      <div className="text-gray-500 mt-1">{icon}</div>

      <div className="flex-1">
        <div className="text-sm text-gray-600 font-medium mb-1">
          {label}
        </div>

        <div
          className={`font-medium ${isLocked ? "filter blur-sm select-none text-gray-900" : "text-gray-900"
            }`}
        >
          {isLocked ? "Hidden Information" : value}
        </div>
      </div>
    </div>
  );



  return (
    <motion.div
      ref={profileRef}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="max-w-7xl mx-auto px-4 py-6"
    >
      {/* Screenshot Protection Overlay */}
      {showScreenshotWarning && (
        <div className="fixed inset-0 bg-white z-[9999] flex items-center justify-center flex-col">
          <div className="text-3xl font-bold text-red-600 mb-4">
            Screenshots are Protected
          </div>
          <p className="text-gray-600">
            Taking screenshots of profiles is not allowed.
          </p>
        </div>
      )}

      <style>{`
                @media print {
                    body { display: none !important; }
                }
            `}</style>

      {/* Header with Back Button */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-rose-600 font-medium transition-colors"
        >
          <FaArrowLeft className="mr-2" /> Back to Matches
        </button>

        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            Profile ID: {profile._id?.slice(-8).toUpperCase()}
          </span>
          {profile.isVerified && (
            <span className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm">
              <FaCheckCircle /> Verified
            </span>
          )}
        </div>
      </div>

      {/* Main Profile Card */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-8">
        {/* Profile Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Profile Photo */}
            <div className="relative">
              <div className="relative w-40 h-40 rounded-lg overflow-hidden border-4 border-white shadow-lg">
                <img
                  src={
                    profile.profilePhoto
                      ? profile.profilePhoto.startsWith("http") ||
                        profile.profilePhoto.startsWith("blob:") ||
                        profile.profilePhoto.startsWith("data:")
                        ? profile.profilePhoto
                        : `${baseUrl}${profile.profilePhoto}`
                      : profile.basicInfo?.gender === "Female"
                        ? "https://randomuser.me/api/portraits/women/85.jpg"
                        : "https://randomuser.me/api/portraits/men/85.jpg"
                  }
                  alt={profile.basicInfo?.firstName || "Profile"}
                  className="w-full h-full object-cover"
                  draggable="false"
                  onError={(e) => {
                    e.currentTarget.src =
                      profile.basicInfo?.gender === "Female"
                        ? "https://randomuser.me/api/portraits/women/85.jpg"
                        : "https://randomuser.me/api/portraits/men/85.jpg";
                  }}
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />


                {/* Subtle diagonal center watermark - much more professional */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-white text-xl font-semibold tracking-widest opacity-30 rotate-[-30deg] select-none">
                    ST Matchzone
                  </span>
                </div>
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              <div className="flex flex-col md:flex-row justify-between items-start mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-1">
                    {profile.basicInfo?.firstName} {profile.basicInfo?.lastName}
                  </h1>
                  <div className="flex items-center gap-4 text-gray-600 mb-3">
                    <span className="flex items-center gap-1">
                      <FaBirthdayCake /> {age} years
                    </span>
                    <span className="flex items-center gap-1">
                      <FaMapMarkerAlt /> {profile.location?.city},{" "}
                      {profile.location?.state}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-rose-50 text-rose-700 rounded-full text-sm flex items-center gap-1">
                      <FaVenusMars /> {profile.basicInfo?.gender}
                    </span>
                    <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm flex items-center gap-1">
                      <FaGraduationCap /> {profile.educationCareer?.education}
                    </span>
                    <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm flex items-center gap-1">
                      <FaBriefcase /> {profile.educationCareer?.occupation}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-4 md:mt-0">
                  <button
                    onClick={handleShortlist}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isShortlisted
                      ? "bg-rose-600 text-white hover:bg-rose-700"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                  >
                    {isShortlisted ? (
                      <FaHeart className="text-red-400" />
                    ) : (
                      <FaRegHeart />
                    )}
                    {isShortlisted ? "Shortlisted" : "Shortlist"}
                  </button>
                  <button
                    onClick={async () => {
                      if (!planFeatures.chatAccess) {
                        onUpgradeClick?.();
                        return;
                      }


                      if (sendingInterest) return;
                      setSendingInterest(true);
                      try {
                        const targetUserId =
                          typeof profile.userId === "object"
                            ? profile.userId._id
                            : profile.userId;

                        const res = await api.post("/chat/interest/send", {
                          targetUserId,
                        });

                        const conv = res.data.conversation || res.data;
                        onOpenConversation?.(conv?._id || conv?.id || null);
                      } catch (error) {
                        console.error("Error sending interest:", error);
                      } finally {
                        setSendingInterest(false);
                      }
                    }}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium
${!planFeatures.chatAccess
                        ? "bg-gray-600 text-white hover:bg-gray-700"
                        : "bg-rose-600 text-white hover:bg-rose-700"
                      }`}

                  >
                    {!planFeatures.chatAccess ? <FaLock /> : <FaHeart />}
                    {!planFeatures.chatAccess
                      ? "Upgrade to Connect"
                      : sendingInterest
                        ? "Sending..."
                        : "Send Interest"}

                  </button>

                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-6">
          <StatCard
            icon={<FaPrayingHands className="text-purple-600" />}
            label="Religion"
            value={profile.religionCommunity?.religion || "N/A"}
          />

          <StatCard
            icon={<FaRulerVertical className="text-green-600" />}
            label="Height"
            value={profile.basicInfo?.heightFeet || "N/A"}
          />
          <StatCard
            icon={<FaWeight className="text-orange-600" />}
            label="Weight"
            value={profile.basicInfo?.weight || "N/A"}
          />
        </div>
      </div>

      {/* Profile Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Details Card */}
          <DetailCard
            title="Personal Details"
            icon={<FaUserCheck className="text-rose-600" />}
            items={[
              {
                icon: <FaBirthdayCake />,
                label: "Date of Birth",
                value: profile.basicInfo?.dob
                  ? new Date(profile.basicInfo.dob).toLocaleDateString()
                  : "N/A",
              },
              {
                icon: <FaVenusMars />,
                label: "Gender",
                value: profile.basicInfo?.gender,
              },
              {
                icon: <FaCalendarAlt />,
                label: "Marital Status",
                value: profile.basicInfo?.maritalStatus,
              },
              {
                icon: <FaTint />,
                label: "Complexion",
                value: profile.basicInfo?.complexion,
              },
              {
                icon: <FaUserFriends />,
                label: "Body Type",
                value: profile.basicInfo?.bodyType,
              },
            ]}
          />

          {/* Religious & Horoscope Details */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FaPrayingHands className="text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Religion & Horoscope
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Religion */}
              {profile.religionCommunity?.religion && (
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <FaPrayingHands className="text-gray-500 mt-1" />
                  <div>
                    <div className="text-sm text-gray-600 font-medium mb-1">Religion</div>
                    <div className="font-medium text-gray-900">
                      {profile.religionCommunity.religion}
                    </div>
                  </div>
                </div>
              )}

              {/* Caste */}
              {profile.religionCommunity?.caste && (
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <FaIdCard className="text-gray-500 mt-1" />
                  <div>
                    <div className="text-sm text-gray-600 font-medium mb-1">Caste</div>
                    <div className="font-medium text-gray-900">
                      {profile.religionCommunity.caste}
                    </div>
                  </div>
                </div>
              )}

              {/* 🔮 Horoscope Fields (LOCKABLE) */}
              <LockedField
                label="Star"
                icon={<FaStar />}
                value={profile.horoscope?.star}
                isLocked={userPlan === "free"}
              />

              <LockedField
                label="Rasi"
                icon={<FaStar />}
                value={profile.horoscope?.rasi}
                isLocked={userPlan === "free"}
              />

              <LockedField
                label="Dosham"
                icon={<FaShieldAlt />}
                value={profile.horoscope?.dosham}
                isLocked={userPlan === "free"}
              />
              {userPlan === "free" && (
                <button
                  onClick={onUpgradeClick}
                  className="mt-6 w-full py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 flex items-center justify-center gap-2"
                >
                  <FaLock />
                  Upgrade to Unlock Horoscope
                </button>
              )}


            </div>
          </div>
          {/* Education & Career */}
          <DetailCard
            title="Education & Career"
            icon={<FaGraduationCap className="text-blue-600" />}
            items={[
              {
                icon: <FaGraduationCap />,
                label: "Education",
                value: profile.educationCareer?.education,
              },
              {
                icon: <FaUniversity />,
                label: "College/University",
                value:
                  profile.educationCareer?.college ||
                  profile.educationCareer?.university,
              },
              {
                icon: <FaBriefcase />,
                label: "Occupation",
                value: profile.educationCareer?.occupation,
              },

              {
                icon: <FaMoneyBillWave />,
                label: "Annual Income",
                value: profile.educationCareer?.income,
              },
              {
                icon: <FaCity />,
                label: "Company",
                value: profile.educationCareer?.company,
              },
            ]}
          />
          {profile.jathagamPhoto && (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaStar className="text-purple-600" />
                Jathagam
              </h3>

              <BlurWrapper locked={userPlan === "free"}>
                <img
                  src={
                    profile.jathagamPhoto.startsWith("data:")
                      ? profile.jathagamPhoto
                      : `${baseUrl}${profile.jathagamPhoto}`
                  }
                  alt="Jathagam"
                  className="w-full max-h-[400px] object-contain rounded-lg border"
                  draggable="false"
                />
              </BlurWrapper>

              {userPlan === "free" && (
                <button
                  onClick={onUpgradeClick}
                  className="mt-4 w-full py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700"
                >
                  Upgrade to View Jathagam
                </button>
              )}
            </div>
          )}
          {profile.photos?.length > 0 && (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaCamera className="text-blue-600" />
                Photos
              </h3>

              <BlurWrapper locked={userPlan === "free"}>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {profile.photos.map((photo, index) => (
                    <img
                      key={index}
                      src={
                        photo.startsWith("data:")
                          ? photo
                          : photo.startsWith("http")
                            ? photo
                            : `${baseUrl}${photo}`
                      }
                      className="w-full h-32 object-cover rounded-lg border"
                      draggable="false"
                      alt={`Photo ${index + 1}`}
                    />
                  ))}
                </div>
              </BlurWrapper>

              {userPlan === "free" && (
                <button
                  onClick={onUpgradeClick}
                  className="mt-4 w-full py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700"
                >
                  Upgrade to View Photos
                </button>
              )}
            </div>
          )}




          {/* Family Details */}
          <DetailCard
            title="Family Details"
            icon={<FaHome className="text-green-600" />}
            items={[
              {
                icon: <FaUserFriends />,
                label: "Father's Occupation",
                value: profile.family?.fatherOccupation,
              },
              {
                icon: <FaUserFriends />,
                label: "Mother's Occupation",
                value: profile.family?.motherOccupation,
              },
              {
                icon: <FaUsers />,
                label: "Brothers",
                value: profile.family?.noOfBrothers,
              },
              {
                icon: <FaUsers />,
                label: "Sisters",
                value: profile.family?.noOfSisters,
              },
              {
                icon: <FaHome />,
                label: "Family Type",
                value: profile.family?.familyType,
              },
              {
                icon: <FaMoneyBillWave />,
                label: "Family Value",
                value: profile.family?.familyValues,
              },
            ]}
          />
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Contact Information */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaPhoneAlt className="text-rose-600" />
              Contact Information
            </h3>
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3 mb-1">
                  <FaPhoneAlt className="text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">
                    Mobile Number
                  </span>
                </div>
                <div
                  className={`font-medium text-gray-900 ${!contactRevealed ? "filter blur-md select-none" : ""
                    }`}
                >
                  {contactRevealed ? contactInfo?.mobile : "XXXXX XXXXX"}
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3 mb-1">
                  <FaEnvelope className="text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">
                    Email Address
                  </span>
                </div>
                <div
                  className={`font-medium text-gray-900 ${!contactRevealed ? "filter blur-md select-none" : ""
                    }`}
                >
                  {contactRevealed ? contactInfo?.email : "xxxx@xxxx.com"}
                </div>
              </div>

              {!contactRevealed && (
                <button
                  onClick={handleRevealContact}
                  disabled={loading}
                  className="w-full py-3 bg-rose-600 text-white font-medium rounded-lg hover:bg-rose-700 transition-colors flex items-center justify-center gap-2 mt-4"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaEye /> Reveal Contact Details
                    </>
                  )}
                </button>
              )}
              {contactError && (
                <div className="mt-3 text-sm text-red-600 font-medium flex items-start gap-2">
                  <span>⚠</span>
                  <span>{contactError}</span>
                </div>
              )}
            </div>
          </div>

          {/* Location Information */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaMapMarkerAlt className="text-blue-600" />
              Location
            </h3>
            <div className="space-y-3">
              <InfoRow
                icon={<FaCity />}
                label="City"
                value={profile.location?.currentCity}
              />
              <InfoRow
                icon={<FaMapMarkerAlt />}
                label="State"
                value={profile.location?.state}
              />
              <InfoRow
                icon={<FaGlobe />}
                label="Country"
                value={profile.location?.country}
              />
              <InfoRow
                icon={<FaHome />}
                label="Native Place"
                value={profile.location?.familyOrigin || "Not specified"}
              />
            </div>
          </div>

          {/* Lifestyle */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaInfoCircle className="text-green-600" />
              Lifestyle
            </h3>
            <div className="space-y-3">
              <InfoRow
                icon={<FaUtensils />}
                label="Diet"
                value={profile.lifestyle?.diet || "Not specified"}
              />
              <InfoRow
                icon={<FaSmoking />}
                label="Smoking"
                value={profile.lifestyle?.smoking || "Not specified"}
              />
              <InfoRow
                icon={<FaWineGlassAlt />}
                label="Drinking"
                value={profile.lifestyle?.drinking || "Not specified"}
              />
            </div>
          </div>

        </div>
      </div>
    </motion.div>
  );
};

// Reusable Components
const StatCard = ({ icon, label, value }) => (
  <div className="bg-gray-50 rounded-lg p-4 text-center">
    <div className="flex justify-center text-2xl mb-2">{icon}</div>
    <div className="text-sm text-gray-600 font-medium mb-1">{label}</div>
    <div className="font-bold text-gray-900">{value}</div>
  </div>
);

const DetailCard = ({ title, icon, items }) => {
  // Remove items with empty / null / undefined values
  const filteredItems = items.filter(
    (item) =>
      item.value !== undefined &&
      item.value !== null &&
      item.value !== "" &&
      item.value !== "Not Specified"
  );

  // If no valid items, don't render the card at all
  if (filteredItems.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gray-100 rounded-lg">{icon}</div>
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredItems.map((item, index) => (
          <div
            key={index}
            className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
          >
            <div className="text-gray-500 mt-1">{item.icon}</div>
            <div>
              <div className="text-sm text-gray-600 font-medium mb-1">
                {item.label}
              </div>
              <div className="font-medium text-gray-900">
                {item.value}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
    <div className="flex items-center gap-2 text-gray-700">
      {icon && <span className="text-gray-500">{icon}</span>}
      <span className="font-medium">{label}</span>
    </div>
    <span className="font-medium text-gray-900">{value}</span>
  </div>
);

export default ProfileDetails;
